import React, { useContext, useMemo, useState } from 'react';
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';
import { Popconfirm, Button, Modal, Select, Flex, Alert, Form, message } from 'antd';
import refreshActiveAdmin from '../../../domain/use_cases/admins/refreshActiveAdmin';
import { ReloadOutlined } from '@ant-design/icons';

const HEALTHCHECK_URL = import.meta.env.VITE_SERVICE_HEALTHCHECK_URL as string;
const HEALTHCHECK_SERVICE_URL = new URL(HEALTHCHECK_URL).origin;

const BACKEND_URL = import.meta.env.VITE_LEEDPULSE_API_URL as string;
const BACKEND_SERVICE_URL = new URL(BACKEND_URL).origin;

const HEALTHCHECK_SELF_ENTRY: HealthcheckServiceDto = {
    name: 'Backend healthcheck',
    url: HEALTHCHECK_SERVICE_URL,
    lastStatus: null,
};

const BACKEND_SELF_ENTRY: HealthcheckServiceDto = {
    name: 'Backend',
    url: BACKEND_SERVICE_URL,
    lastStatus: null,
};

interface HealthcheckServiceDto {
    name: string;
    url: string | null;
    lastStatus: 'OK' | 'ERROR' | null;
}

export default function AdminRefreshButton({ env, style }: { env: string, style?: React.CSSProperties }) {
    const authContext = useContext(AuthenticationContext);

    const [open, setOpen] = useState(false);
    const [services, setServices] = useState<HealthcheckServiceDto[]>([]);
    const [isLoadingServices, setIsLoadingServices] = useState(false);
    const [selectedServiceUrl, setSelectedServiceUrl] = useState<string | null>(null);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const fetchServices = async () => {
        try {
            setIsLoadingServices(true);
            const res = await fetch(`${HEALTHCHECK_URL}/status`);
            if (!res.ok) return;

            const json = await res.json() as { services: HealthcheckServiceDto[] };
            if (!json?.services || !Array.isArray(json.services)) return;

            const withUrl = json.services.filter((s) => s.url !== null);
            
            // Backend-healthcheck return url in this form : http://localhost:3050/api/v1/info -> we need to keep only "http://localhost:3050"
            const withUrlCleaned = withUrl.map((s) => {
                const url = new URL(s.url as string);
                return {
                    ...s,
                    url: url.origin,
                };
            });

            // Always prepend the healthcheck service itself (not returned by its own /status)
            const all = [HEALTHCHECK_SELF_ENTRY, ...withUrlCleaned];
            setServices(all);

            if (!selectedServiceUrl) {
                setSelectedServiceUrl(all[0].url);
            }
        } catch {
            message.error('Impossible de récupérer la liste des services.');
            setServices([BACKEND_SELF_ENTRY]);
        } finally {
            setIsLoadingServices(false);
        }
    };

    const handleOpen = () => {
        setOpen(true);
        fetchServices();
    };

    const handleRefresh = async () => {
        if (!selectedServiceUrl) return;
        try {
            setIsRefreshing(true);
            await refreshActiveAdmin(selectedServiceUrl, env, authContext.authentication);
        } finally {
            setIsRefreshing(false);
            setOpen(false);
        }
    };

    const selectOptions = useMemo(() => services.map((s) => ({
            label: s.name,
            value: s.url,
        })), [services]);

    return <>
        <Button icon={<ReloadOutlined />} type="default" style={{ marginLeft: "1rem", ...style }} onClick={handleOpen}>
            Rafraîchir la configuration
        </Button>

        <Modal
            title="Rafraîchir la configuration d'un service"
            open={open}
            onCancel={() => setOpen(false)}
            footer
        >
            <Flex vertical gap={16}>
                <Alert type='warning' showIcon message="Cette action recharge la configuration depuis le document Firestore." />

                <Form.Item label="Service">
                    <Select
                        loading={isLoadingServices}
                        options={selectOptions}
                        value={selectedServiceUrl}
                        onChange={(value) => setSelectedServiceUrl(value)}
                        placeholder="Sélectionner un service..."
                        notFoundContent={isLoadingServices ? 'Chargement...' : 'Aucun service disponible'}
                    />
                </Form.Item>

                <Flex justify="flex-end" gap={8}>
                    <Button onClick={() => setOpen(false)}>Annuler</Button>

                    <Popconfirm
                        title="Rafraîchir la configuration ?"
                        description="Cette action recharge la configuration depuis le document Firestore."
                        okText="Rafraîchir"
                        cancelText="Annuler"
                        onConfirm={handleRefresh}
                        disabled={!selectedServiceUrl}
                    >
                        <Button
                            icon={<ReloadOutlined />}
                            type="primary"
                            loading={isRefreshing}
                            disabled={!selectedServiceUrl}
                            style={{ marginRight: "1rem" }}
                        >
                            Rafraîchir
                        </Button>
                    </Popconfirm>
                </Flex>
            </Flex>
        </Modal>
    </>;
}
